"use client";

import { useState } from "react";
import { useSession, signOut } from "next-auth/react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { Link } from "@/i18n/routing";
import { useUser } from "@/contexts/UserContext";
import MobileDrawer from "./MobileDrawer";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Header() {
  const { data: session, status } = useSession();
  const { userData } = useUser();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const t = useTranslations("common");

  const isLoggedIn = status === "authenticated" && !!session?.user;
  const userName = userData.name || session?.user?.name || "User";

  const menuItems = [
    { href: "/", label: t("home") },
    { href: "/o-nama", label: t("about") },
    { href: "/proizvodi", label: t("products") },
    { href: "/usluge", label: t("services") },
    { href: "/download", label: t("download") },
    { href: "/kontakt", label: t("contact") },
  ];

  const handleSignOut = () => {
    // Use absolute URL to avoid redirecting to localhost
    const callbackUrl = typeof window !== "undefined" 
      ? `${window.location.origin}/` 
      : "/";
    signOut({ callbackUrl });
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-secondary-200/50 shadow-sm">
        <nav 
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8"
          aria-label="Main navigation"
        >
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Left side - Hamburger (mobile) + Logo */}
            <div className="flex items-center gap-3">
              {/* Hamburger Menu Button - Mobile only */}
              <button
                type="button"
                className="md:hidden p-2 -ml-2 rounded-lg text-secondary-600 hover:text-primary-500 hover:bg-secondary-100 transition-colors"
                onClick={() => setIsDrawerOpen(true)}
                aria-label={t("openMenu")}
                aria-expanded={isDrawerOpen}
                aria-controls="mobile-menu"
              >
                <svg 
                  className="w-6 h-6" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M4 6h16M4 12h16M4 18h16" 
                  />
                </svg>
              </button>

              {/* Logo - Hidden on mobile (shown in center), visible on desktop */}
              <Link 
                href="/" 
                className="hidden md:flex items-center"
                aria-label="Real Impeks - Home"
              >
                <Image
                  src="/images/logo.png"
                  alt="Real Impeks"
                  width={275}
                  height={79}
                  className="h-10 lg:h-12 w-auto"
                  priority
                />
              </Link>
            </div>

            {/* Center - Logo (mobile) / Main Menu (desktop) */}
            <div className="flex-1 flex items-center justify-center md:justify-center">
              {/* Mobile Logo - Centered */}
              <Link 
                href="/" 
                className="md:hidden flex items-center"
                aria-label="Real Impeks - Home"
              >
                <Image
                  src="/images/logo.png"
                  alt="Real Impeks"
                  width={275}
                  height={79}
                  className="h-8 w-auto"
                  priority
                />
              </Link>

              {/* Desktop Main Menu */}
              <ul className="hidden md:flex items-center gap-1 lg:gap-2">
                {menuItems.map((item) => (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      className="px-3 lg:px-4 py-2 text-sm lg:text-base font-medium text-secondary-700 hover:text-primary-500 hover:bg-primary-50 rounded-lg transition-colors"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right side - Language Switcher + Auth section */}
            <div className="flex items-center gap-2">
              {/* Language Switcher - Desktop */}
              <div className="hidden md:block">
                <LanguageSwitcher />
              </div>

              {isLoggedIn ? (
                /* Logged in - User Avatar with Dropdown */
                <div className="relative">
                  <button
                    type="button"
                    className="flex items-center gap-2 p-1 rounded-full hover:bg-secondary-100 transition-colors"
                    onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                    aria-label={t("userMenu", { name: userName })}
                    aria-expanded={isUserMenuOpen}
                  >
                    <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-primary-500 flex items-center justify-center text-white font-semibold text-sm ring-2 ring-primary-100">
                      {userName.charAt(0).toUpperCase()}
                    </div>
                    <svg 
                      className={`hidden md:block w-4 h-4 text-secondary-500 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* User Dropdown Menu */}
                  {isUserMenuOpen && (
                    <>
                      {/* Backdrop */}
                      <div 
                        className="fixed inset-0 z-10" 
                        onClick={() => setIsUserMenuOpen(false)}
                      />
                      
                      {/* Dropdown */}
                      <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-secondary-200 py-2 z-20">
                        {/* User Info */}
                        <div className="px-4 py-3 border-b border-secondary-100">
                          <p className="font-medium text-secondary-900 truncate">{userName}</p>
                          <p className="text-sm text-secondary-500 truncate">{userData.email || session?.user?.email}</p>
                        </div>

                        {/* Menu Items */}
                        <div className="py-1">
                          <Link
                            href="/dashboard"
                            className="flex items-center gap-3 px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                            </svg>
                            {t("dashboard") || "Dashboard"}
                          </Link>
                          <Link
                            href="/profile"
                            className="flex items-center gap-3 px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            {t("profile") || "Profil"}
                          </Link>
                        </div>

                        {/* Sign Out */}
                        <div className="border-t border-secondary-100 pt-1">
                          <button
                            onClick={handleSignOut}
                            className="flex items-center gap-3 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                            </svg>
                            {t("logout") || "Odjavi se"}
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                /* Not logged in - Login/Register buttons */
                <div className="hidden md:flex items-center gap-2">
                  <Link
                    href="/login"
                    className="px-4 py-2 text-sm font-medium text-secondary-700 hover:text-primary-500 hover:bg-secondary-100 rounded-lg transition-colors"
                  >
                    {t("login")}
                  </Link>
                  <Link
                    href="/register"
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg transition-colors"
                  >
                    {t("register")}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile Drawer */}
      <MobileDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        menuItems={menuItems}
        isLoggedIn={isLoggedIn}
        userName={userName}
        onSignOut={handleSignOut}
      />
    </>
  );
}
